import { useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { BookOpen, Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  PageHeaderSkeleton,
  TableSkeleton,
} from "~/components/ui/skeletons";
import { useDeleteMaquilaRecipe, useMaquilaRecipes } from "~/hooks/maquila";
import type { MaquilaProductRecipe } from "~/types/maquila";

import { MaquilaRecipeDialog } from "./components/MaquilaRecipeDialog";

export const MaquilaRecipesPage = () => {
  const navigate = useNavigate();
  const { data: recipes = [], isLoading } = useMaquilaRecipes();
  const deleteMutation = useDeleteMaquilaRecipe();

  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MaquilaProductRecipe | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipes;
    return recipes.filter(
      (r) =>
        r.product_name.toLowerCase().includes(q) ||
        r.product_code?.toLowerCase().includes(q) ||
        r.client?.name?.toLowerCase().includes(q)
    );
  }, [recipes, search]);

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (recipe: MaquilaProductRecipe) => {
    setEditing(recipe);
    setDialogOpen(true);
  };

  const handleDelete = (recipe: MaquilaProductRecipe) => {
    if (
      window.confirm(
        `¿Eliminar la receta "${recipe.product_name}"? No afecta consumos ya registrados.`
      )
    ) {
      deleteMutation.mutate(recipe.id);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeaderSkeleton withAction />
        <TableSkeleton rows={8} columns={4} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Recetas de Maquila"
        description="Insumos de Aliace que consume cada producto del cliente de maquila"
        back={
          <Button
            variant="ghost"
            className="h-8 px-2 text-muted-foreground"
            onClick={() => navigate("/maquila")}
          >
            ← Volver
          </Button>
        }
        actions={
          <Button onClick={openNew}>
            <Plus className="mr-2 h-4 w-4" />
            Nueva receta
          </Button>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <Input
          className="h-9 sm:w-80"
          placeholder="Buscar por producto, código o cliente…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={BookOpen}
                title={
                  recipes.length === 0
                    ? "Sin recetas de maquila"
                    : "Sin resultados"
                }
                description={
                  recipes.length === 0
                    ? "Crea una receta para definir los insumos que consume cada producto del cliente."
                    : "No hay recetas que coincidan con la búsqueda."
                }
                action={
                  recipes.length === 0 ? (
                    <Button onClick={openNew}>
                      <Plus className="mr-2 h-4 w-4" />
                      Nueva receta
                    </Button>
                  ) : undefined
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Producto</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead className="text-center">Insumos</TableHead>
                    <TableHead className="w-24 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((recipe) => (
                    <TableRow key={recipe.id} className="hover:bg-muted/30">
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{recipe.product_name}</span>
                          {recipe.product_code && (
                            <span className="font-mono text-xs text-muted-foreground">
                              {recipe.product_code}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {recipe.client?.name ?? "—"}
                      </TableCell>
                      <TableCell className="text-center">
                        {recipe.ingredients?.length ?? 0}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEdit(recipe)}
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDelete(recipe)}
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <MaquilaRecipeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        recipe={editing}
      />
    </PageContainer>
  );
};
